import { Controller } from 'stimulus';
import Swiper from 'swiper';
import { EffectCoverflow } from 'swiper/modules';
import loadYoutubeApi from '../helpers/loadYoutubeApi';
import loadVimeoApi from '../helpers/loadVimeoApi';
import loadVimeoPoster from '../helpers/loadVimeoPoster';
import dataLayer from '../tracking/dataLayer';

class VideoSlider extends Controller {
  connect() {
    Swiper.use([EffectCoverflow]);

    this.swiper = new Swiper(this.element, {
      slidesPerView: 1,
      loop: true,
      loopedSlides: 2,
      effect: 'coverflow',
      centeredSlides: true,
      slideToClickedSlide: true,
      threshold: 30,
      coverflowEffect: {
        depth: 600,
        rotate: 0,
      },
    });

    this.swiper.on('slideChangeTransitionEnd', () => {
      // Find all slides where the class swiper-video-active is set. This indicates
      // that a video can be started for those slides (to prevent users from
      // starting videos on non-active slides).
      const videoActiveSlides = Array.prototype.slice.call(this.swiper.wrapperEl.querySelectorAll('.swiper-video-active'));

      videoActiveSlides.forEach((videoActiveSlide) => {
        // Remove the video active class
        videoActiveSlide.classList.remove('swiper-video-active');

        // Stop videos, just to be sure
        this.stopVideo(videoActiveSlide);
      });

      // Add the active class to the active slide. Users can start a video on this slide.
      this.swiper.slides[this.swiper.activeIndex].classList.add('swiper-video-active');
    });

    // If autoplay is enabled, we detect the scroll position and when the element comes into view
    if (this.data.get('autoplay') === 'true') {
      this.initializeScrollObserver();
    }

    // Initialize poster images, if they were not given from the CMS
    this.initializePosters();

    const activeSlide = typeof this.swiper.slides !== 'undefined' && this.swiper.activeIndex >= 0
      ? this.swiper.slides[this.swiper.activeIndex]
      : undefined;

    if (activeSlide) {
      activeSlide.classList.add('swiper-video-active');
    }
  }

  disconnect() {
    if (this.swiper) {
      this.swiper.destroy();
    }
  }

  initializeScrollObserver() {
    // 'Listen' to the scroll position and retrieve callback when the
    // visibility of this.element changes.
    const observer = new IntersectionObserver((entries) => {
      this.onElementChangeVisibility(entries[0].isIntersecting);
    });

    observer.observe(this.element);
  }

  // eslint-disable-next-line class-methods-use-this
  onElementChangeVisibility(visible) {
    if (!visible) {
      return;
    }

    // The element is in view, find the active slide
    const activeSlide = this.swiper.slides[this.swiper.activeIndex];

    // If an active slide is found, start the video.
    if (activeSlide) {
      const poster = activeSlide.querySelector('[data-video-service]');

      if (poster) {
        this.toggleVideo(activeSlide, poster.dataset.videoService, poster.dataset.videoId);
      }
    }
  }

  onVideoClick(event) {
    const { currentTarget } = event;

    // Find the wrapper slide element
    const slide = currentTarget.closest('.swiper-slide');

    if (!slide) {
      return;
    }

    // Only allow starting the video when the slide is active. This class is given
    // when the slides becomes active. To prevent users from starting videos on
    // non-active slides.
    if (slide.className.indexOf('swiper-video-active') < 0) {
      return;
    }

    // Start the video
    this.toggleVideo(slide, currentTarget.dataset.videoService, currentTarget.dataset.videoId);
  }

  toggleVideo(slide, videoSource, videoId, muted = false) {
    dataLayer.event('video', 'play', `${videoSource}:${videoId}`);

    const hasIframe = !!slide.querySelector('iframe');

    // If the slide already has an iframe, it probably means the video
    // has already been initiated or started. Nothing to do here.
    if (hasIframe) {
      return;
    }

    const element = document.createElement('div');
    element.classList.add('video-carousel__video-element');

    slide.appendChild(element);

    // Create a Vimeo/YouTube video element, connected with an API
    this.createVideo(element, videoSource, videoId).then((video) => {
      if (videoSource === 'youtube') {
        if (muted) {
          video.mute();
        }

        video.playVideo();
      } else if (videoSource === 'vimeo') {
        if (muted) {
          video.setMuted(true);
        }

        video.play();
      }
    });
  }

  createVideo(element, videoSource, videoId) {
    return new Promise((resolve) => {
      if (videoSource === 'youtube') {
        loadYoutubeApi()
          .then(() => this.createYoutubePlayer(element, videoId))
          .then(resolve);
      } else if (videoSource === 'vimeo') {
        loadVimeoApi()
          .then(() => this.createVimeoPlayer(element, videoId))
          .then(resolve);
      }
    });
  }

  // eslint-disable-next-line class-methods-use-this
  stopVideo(slide) {
    const videoElement = slide.querySelector('.video-carousel__video-element');

    if (videoElement) {
      videoElement.parentNode.removeChild(videoElement);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  createYoutubePlayer(element, videoId) {
    return new Promise((resolve) => {
      const player = new window.YT.Player(element, {
        width: '640',
        height: '360',
        videoId,
        playerVars: {
          playsinline: 1,
          alt: 0,
        },
        events: {
          onReady: () => resolve(player),
        },
      });
    });
  }

  // eslint-disable-next-line class-methods-use-this
  createVimeoPlayer(element, videoId) {
    const player = new window.Vimeo.Player(element, {
      id: videoId,
    });

    return player;
  }

  initializePosters() {
    // Find all poster elements in the slider.
    this.posterTargets.forEach((poster) => {
      const img = poster.querySelector('img');

      // If an image is found in the poster, the poster image is already available.
      // Nothing left to do.
      if (img) {
        return;
      }

      const videoSource = poster.getAttribute('data-video-service');
      const videoId = poster.getAttribute('data-video-id');

      if (videoSource === 'youtube') {
        // Add a YouTube poster image
        this.createPoster(poster, `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`);
      } else if (videoSource === 'vimeo') {
        // Add a Vimeo poster image. Vimeo only allows retrieving thumbnails
        // from the API.
        loadVimeoPoster(videoId).then((url) => {
          this.createPoster(poster, url);
        });
      }
    });
  }

  // eslint-disable-next-line class-methods-use-this
  createPoster(element, url) {
    if (!url) {
      return;
    }

    // Create a wrapper element to force the image into a 16/9 ratio.
    const posterWrap = document.createElement('div');
    posterWrap.style.position = 'relative';
    posterWrap.style.paddingTop = '56.25%';

    const img = document.createElement('img');
    img.loading = 'lazy';
    img.src = url;
    img.style.display = 'block';
    img.style.width = '100%';
    img.style.height = '100%';
    img.style.position = 'absolute';
    img.style.top = '0';
    img.style.left = '0';
    img.style.objectFit = 'cover';

    posterWrap.appendChild(img);

    element.prepend(posterWrap);
  }
}

VideoSlider.targets = [
  'leftArrow',
  'rightArrow',
  'poster',
];

export default VideoSlider;
